import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Badge } from "@/lib/ui";
import { CheckCircle2, XCircle, FileText } from "lucide-react";

const statusColor = { pending: "gold", approved: "green", rejected: "red" };

function ReviewPanel({ id, onDone }) {
  const [v, setV] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/admin/verifications/${id}`).then((r) => setV(r.data)); }, [id]);

  const review = async (approve) => {
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${id}/review`, { approve, note });
      toast.success(approve ? "Helper verified — badge granted" : "Verification rejected");
      onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!v) return <p className="p-5 text-slate-500">Loading documents…</p>;
  return (
    <div data-testid="verification-review-panel" className="p-5 bg-[#FAF8F5] space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <p className="font-semibold text-slate-800 mb-2">Government ID</p>
          <img data-testid="review-id-image" src={v.id_document} alt="ID document" className="rounded-xl border border-[#E2D9CE] max-h-72" />
        </div>
        <div>
          <p className="font-semibold text-slate-800 mb-2">Background check</p>
          {v.background_document ? (
            <img data-testid="review-bg-image" src={v.background_document} alt="Background document" className="rounded-xl border border-[#E2D9CE] max-h-72" />
          ) : <p className="text-slate-500">Not provided</p>}
        </div>
      </div>
      {v.note && <p className="text-slate-700"><span className="font-semibold">Helper's note:</span> {v.note}</p>}
      {v.status === "pending" && (
        <>
          <input data-testid="review-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to helper (optional, shown if rejected)"
            className="w-full px-4 min-h-[48px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] outline-none" />
          <div className="flex gap-3">
            <button data-testid="approve-verification-btn" disabled={busy} onClick={() => review(true)}
              className="flex items-center gap-2 bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-semibold px-5 min-h-[48px] rounded-xl transition-colors">
              <CheckCircle2 className="w-5 h-5" /> Approve
            </button>
            <button data-testid="reject-verification-btn" disabled={busy} onClick={() => review(false)}
              className="flex items-center gap-2 border-2 border-[#E2D9CE] hover:border-red-400 disabled:opacity-60 text-slate-700 font-semibold px-5 min-h-[48px] rounded-xl transition-colors">
              <XCircle className="w-5 h-5" /> Reject
            </button>
          </div>
        </>
      )}
      {v.status !== "pending" && v.admin_note && <p className="text-slate-600"><span className="font-semibold">Admin note:</span> {v.admin_note}</p>}
    </div>
  );
}

export default function AdminVerifications({ onChanged }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/admin/verifications", { params: { status: filter } }).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const done = () => { setOpenId(null); load(); onChanged?.(); };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} data-testid={`verif-filter-${s}`} onClick={() => setFilter(s)}
            className={`px-4 min-h-[44px] rounded-xl font-semibold capitalize transition-all ${filter === s ? "bg-[#2E7D6B] text-white" : "bg-white border border-[#E2D9CE] text-slate-700"}`}>{s}</button>
        ))}
      </div>
      <div className="bg-white border border-[#E2D9CE] rounded-2xl divide-y divide-[#E2D9CE]">
        {items.length === 0 ? <p className="p-6 text-slate-600">No {filter === "all" ? "" : filter} verification requests.</p> : items.map((v) => (
          <div key={v.id} data-testid={`verification-item-${v.id}`}>
            <div className="p-5 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-[#D99B26] text-white flex items-center justify-center"><FileText className="w-6 h-6" /></span>
                <div>
                  <p className="font-semibold text-slate-900">{v.helper_name}</p>
                  <p className="text-slate-500 text-sm">{v.helper_email} · {new Date(v.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge color={statusColor[v.status]}>{v.status}</Badge>
                {v.has_background_document && <Badge color="slate">+ background doc</Badge>}
                <button data-testid={`review-verification-${v.id}`} onClick={() => setOpenId(openId === v.id ? null : v.id)}
                  className="border-2 border-[#E2D9CE] hover:border-[#D96B43] text-slate-800 font-semibold px-4 min-h-[44px] rounded-xl transition-colors">
                  {openId === v.id ? "Close" : "Review"}
                </button>
              </div>
            </div>
            {openId === v.id && <ReviewPanel id={v.id} onDone={done} />}
          </div>
        ))}
      </div>
    </div>
  );
}
